import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldX, Home, LogOut, Stethoscope } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';

export default function UnauthorizedPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation('doctor-platform');

  const handleSignOut = async () => {
    await logout();
    navigate('/auth/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <div className="h-16 w-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center">
              <ShieldX className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
          <CardTitle className="text-2xl">{t('auth.unauthorized.title')}</CardTitle>
          <CardDescription className="mt-2">
            {t('auth.unauthorized.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Medical Portal Branding */}
          <div className="flex justify-center pb-2">
            <div className="flex items-center space-x-2 text-muted-foreground">
              <Stethoscope className="h-5 w-5" />
              <span className="text-sm font-medium">{t('auth.unauthorized.portalLabel')}</span>
            </div>
          </div>

          {/* User Info */}
          {user && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
              <p className="text-sm text-center">
                {t('auth.unauthorized.loggedInAs', { email: user.email })}
              </p>
              {user.role && (
                <p className="text-xs text-muted-foreground text-center mt-1">
                  {t('auth.unauthorized.role', { role: user.role })}
                </p>
              )}
            </div>
          )}

          {/* Explanation */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              {t('auth.unauthorized.explanation')}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full"
            >
              <Home className="mr-2 h-4 w-4" />
              {t('auth.unauthorized.goToDashboard')}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleSignOut}
              className="w-full"
            >
              <LogOut className="mr-2 h-4 w-4" />
              {t('auth.unauthorized.signOut')}
            </Button>
          </div>

          {/* Help Text */}
          <div className="text-center text-sm text-muted-foreground pt-4">
            <p>
              {t('auth.unauthorized.help')}{' '}
              <a href="mailto:support@example.com" className="text-primary hover:underline">
                support@example.com
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
